import {
  ISSUE_MGMT_PERMISSION,
  ISSUE_MGMT_WRITE_PERMISSION,
  KNOWLEDGE_META_PERMISSION,
  KNOWLEDGE_META_WRITE_PERMISSION,
  KNOWLEDGE_SEO_PERMISSION,
  KNOWLEDGE_SEO_WRITE_PERMISSION,
  MEMBER_PERMISSION,
  MEMBER_STAGING_PERMISSION,
  PRODUCT_BACKLOG_PERMISSION,
  PRODUCT_BACKLOG_WRITE_PERMISSION,
  REVENUE_CLOSE_PERMISSION,
} from '@cortex/domain-ac-mens-hub-api';
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpen,
  BrainCircuit,
  Calculator,
  ClipboardCheck,
  Database,
  FileBarChart,
  FileQuestion,
  FileText,
  Filter,
  GitCompareArrows,
  HeadphonesIcon,
  Home,
  Kanban,
  List,
  ListChecks,
  Map as MapIcon,
  MessageSquareText,
  Mic,
  PieChart,
  Rocket,
  Scissors,
  Shield,
  Shirt,
  ShoppingBag,
  Target,
  Ticket,
  TrendingUp,
  Users,
  Wallet,
  Wand2,
} from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub のメニュー項目1件分の定義（ルート・i18n翻訳キー（labelKey）・アイコン・MVPフラグ）。
 * @graph-connects none
 */
export interface NavItemConfig {
  key: string;
  labelKey: string;
  /**
   * このページに入れる**追加の**権限キーと、権限管理画面に出すラベル。
   *
   * `key` と合わせて「いずれか1つでも持っていれば見える」になる。会員一覧のように
   * 本番用とSTG用で別々の権限を持ち、片方だけでもページに入れる必要がある画面で使う。
   * 指定しなければ従来どおり `key` だけで判定する。
   */
  extraPermissions?: readonly { key: string; labelKey: string }[];
  path: string;
  icon: ReactNode;
  /** Phase 1（MVP）で実装予定の項目。 */
  mvp?: boolean;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business サイドメニューのグループ1件分の定義（見出しi18n翻訳キー（labelKey）と項目群）。
 * @graph-connects NavItemConfig [has] グループ配下のメニュー項目
 */
export interface NavGroupConfig {
  labelKey?: string;
  items: NavItemConfig[];
}

/**
 * aC Men's Hub のメニュー（サイドナビ）の単一ソース。
 *
 * 方針: メニュー/トップには **実装済み機能だけ** を載せる。情報設計上の3グループ
 * （📊KPI / 🧩プロダクト / 🛠オペレーション）は今後の地図として docs に持つが、
 * UIには未実装機能を先出ししない。機能が実際に追加されるとき、その機能のPRで
 * 該当グループ（無ければ新設）に項目を1件足す。
 *
 * labelKey は i18n の翻訳キー（common namespace）を指す。
 * 実際の表示テキストは呼び出し側で t(labelKey) に変換する。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub のサイドメニューの単一ソース。実装済み機能のみを載せ、ルートとナビを生成する。
 * @graph-connects NavGroupConfig [has] メニューグループ定義
 */
export const NAV_GROUPS: readonly NavGroupConfig[] = [
  {
    items: [
      {
        key: 'home',
        labelKey: 'nav.home',
        path: '/',
        icon: <Home className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'weekly-report',
        labelKey: 'nav.weeklyReport',
        path: '/weekly-report',
        icon: <FileText className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'key-issues',
        labelKey: 'nav.keyIssues',
        path: '/key-issues',
        icon: <AlertTriangle className="h-5 w-5" aria-hidden="true" />,
      },
      {
        // 動かすキー（product-backlog-write）は画面の中の操作だけを出し分けるので PAGELESS_PERMISSIONS に置く
        key: PRODUCT_BACKLOG_PERMISSION,
        labelKey: 'nav.productBacklog',
        path: '/backlog',
        icon: <Kanban className="h-5 w-5" aria-hidden="true" />,
      },
      {
        // 編集キー（issue-mgmt-write）は画面の中の操作だけを出し分けるので PAGELESS_PERMISSIONS に置く。
        // extraPermissions に入れると、編集キーだけで閲覧キーの無い人にも画面のシェルが見えてしまう
        key: ISSUE_MGMT_PERMISSION,
        labelKey: 'nav.issueMgmt',
        path: '/issue-mgmt',
        icon: <ListChecks className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'reports',
        labelKey: 'nav.reports',
        path: '/reports',
        icon: <FileBarChart className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupKpi',
    items: [
      {
        key: 'kpi-daily',
        labelKey: 'nav.kpiDaily',
        path: '/kpi',
        icon: <BarChart3 className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'budget-actual',
        labelKey: 'nav.budgetActual',
        path: '/budget-actual',
        icon: <Target className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupFinance',
    items: [
      {
        key: 'revenue',
        labelKey: 'nav.revenue',
        path: '/revenue',
        icon: <Wallet className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'cost-accounting',
        labelKey: 'nav.costAccounting',
        path: '/cost-accounting',
        icon: <Calculator className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'purchase-analysis',
        labelKey: 'nav.purchaseAnalysis',
        path: '/purchase-analysis',
        icon: <ShoppingBag className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupMarketing',
    items: [
      {
        key: 'funnel-analysis',
        labelKey: 'nav.funnelAnalysis',
        path: '/funnel-analysis',
        icon: <Filter className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'coupon-usage',
        labelKey: 'nav.couponUsage',
        path: '/coupon-usage',
        icon: <Ticket className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'member-demographics',
        labelKey: 'nav.memberDemographics',
        path: '/member-demographics',
        icon: <PieChart className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: KNOWLEDGE_META_PERMISSION,
        labelKey: 'nav.knowledgeMeta',
        // 投入・無効化は閲覧より狭く配る。ナビ項目は増やさず、権限管理画面からだけ付与する
        extraPermissions: [
          { key: KNOWLEDGE_META_WRITE_PERMISSION, labelKey: 'nav.knowledgeMetaWrite' },
        ],
        path: '/meta-docs',
        icon: <BookOpen className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupStyling',
    items: [
      {
        key: 'item-catalog',
        labelKey: 'nav.itemCatalog',
        path: '/item-catalog',
        icon: <Shirt className="h-5 w-5" aria-hidden="true" />,
      },
      {
        // 在庫状況（/coorde-capacity）も同じ coorde 権限で見る。ナビを2つに割ると
        // 権限キーも2つになり、片方だけ付与された「上限は見えるが作れない」状態が生まれる
        key: 'coorde',
        labelKey: 'nav.coordeHistory',
        path: '/coorde',
        icon: <Wand2 className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'styling-review',
        labelKey: 'nav.stylingReview',
        path: '/styling-review',
        icon: <ClipboardCheck className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'styling-analysis',
        labelKey: 'nav.stylingAnalysis',
        path: '/styling-analysis',
        icon: <Scissors className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'stylist-performance',
        labelKey: 'nav.stylistPerformance',
        path: '/stylist-performance',
        icon: <TrendingUp className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupCs',
    items: [
      {
        key: MEMBER_PERMISSION,
        labelKey: 'nav.member',
        // STG 権限だけを持つ人も会員一覧に入り、STG タブでテストアカウントを探せる
        extraPermissions: [{ key: MEMBER_STAGING_PERMISSION, labelKey: 'nav.memberStaging' }],
        path: '/members',
        icon: <Users className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'withdrawal-analysis',
        labelKey: 'nav.withdrawalAnalysis',
        path: '/withdrawal-analysis',
        icon: <HeadphonesIcon className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'cs-inquiry-dashboard',
        labelKey: 'nav.csInquiryDashboard',
        path: '/cs/inquiry',
        icon: <MessageSquareText className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'cs-inquiry-chats',
        labelKey: 'nav.csInquiryChats',
        path: '/cs/inquiry/chats',
        icon: <List className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'cs-inquiry-ai-analysis',
        labelKey: 'nav.csInquiryAiAnalysis',
        path: '/cs/inquiry/ai-analysis',
        icon: <BrainCircuit className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'faq-monitor',
        labelKey: 'nav.faqMonitor',
        path: '/faq-monitor',
        icon: <FileQuestion className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupProduct',
    items: [
      {
        key: 'code-map',
        labelKey: 'nav.codeMap',
        path: '/code-map',
        icon: <MapIcon className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'release-history',
        labelKey: 'nav.releaseHistory',
        path: '/release-history',
        icon: <Rocket className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'data-catalog',
        labelKey: 'nav.dataCatalog',
        path: '/data-catalog',
        icon: <Database className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupEngineering',
    items: [
      {
        key: 'data-integrity',
        labelKey: 'nav.dataIntegrity',
        path: '/data-integrity',
        icon: <GitCompareArrows className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: 'nav.groupAdmin',
    items: [
      {
        key: 'transcriptions',
        labelKey: 'nav.transcriptions',
        path: '/transcriptions',
        icon: <Mic className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'notifications',
        labelKey: 'nav.notifications',
        path: '/notifications',
        icon: <Bell className="h-5 w-5" aria-hidden="true" />,
      },
      {
        key: 'permissions-admin',
        labelKey: 'nav.permissionsAdmin',
        path: '/permissions',
        icon: <Shield className="h-5 w-5" aria-hidden="true" />,
      },
    ],
  },
];

/**
 * 画面を持たない権限。ナビには出さないが、権限管理画面からは付与できる。
 *
 * 技術SEO公式情報（`knowledge-seo`）のように **API だけを提供して画面を作らない機能**
 * の権限は、どのナビ項目にも紐づかないため `NAV_GROUPS` からは導出できない。ここに
 * 書かないと `ALL_PAGE_KEYS` に載らず、権限管理画面の選択肢に出ないまま「誰にも付与
 * できない権限」になり、Firestore を直接書き換えるしか手が無くなる。
 *
 * `extraPermissions`（会員一覧のSTG権限・Meta公式情報の書き込み権限）との違いは、
 * あちらが「あるページに入れる追加キー」なのに対し、こちらは入る先のページ自体が
 * 無いこと。画面を作るときは `NAV_GROUPS` の項目へ移す。
 *
 * 売上の月の締め（`revenue-close`）のように、既存の画面の中の操作だけを出し分ける権限も
 * ここに置く。`extraPermissions` に入れると「いずれか1つ」の判定でその権限だけでも
 * ページに入れてしまい、閲覧権限の無い人に画面のシェルが見えるため。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 画面を持たない機能の権限キー一覧。ナビには出さず、権限管理画面の選択肢にだけ出す。
 * @graph-connects none
 */
export const PAGELESS_PERMISSIONS: readonly { key: string; labelKey: string }[] = [
  // 技術SEO公式情報。蓄積と参照(BigQuery直読み)が目的で Hub に画面は無いが、
  // 母艦の日次同期 seo-official-sync が API を叩くため実行者への付与手段が要る
  { key: KNOWLEDGE_SEO_PERMISSION, labelKey: 'nav.knowledgeSeo' },
  { key: KNOWLEDGE_SEO_WRITE_PERMISSION, labelKey: 'nav.knowledgeSeoWrite' },
  // 売上の月の締め・締め直し。売上ページ（revenue）の中のボタンだけを出し分ける
  { key: REVENUE_CLOSE_PERMISSION, labelKey: 'nav.revenueClose' },
  // 課題管理の編集。課題管理の画面（閲覧キー）の中の操作だけを出し分ける
  { key: ISSUE_MGMT_WRITE_PERMISSION, labelKey: 'nav.issueMgmtWrite' },
  // 案件を動かす（全社スプシの行の位置・メモを書く）。案件の画面の中の操作だけを出し分ける
  { key: PRODUCT_BACKLOG_WRITE_PERMISSION, labelKey: 'nav.productBacklogWrite' },
];

/**
 * 権限制御用の全ページキー一覧（SSoT: NAV_GROUPS + PAGELESS_PERMISSIONS から自動導出）。
 * 新ページ追加時に NAV_GROUPS にエントリを足すだけで権限管理対象に自動登録される。
 * 画面を持たない機能の権限だけは PAGELESS_PERMISSIONS に足す。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 権限制御用の全ページキー一覧。NAV_GROUPS と画面を持たない権限から自動導出し、ロール管理 UI のページ選択肢として使われる。
 * @graph-connects NAV_GROUPS [references] ページキーを NAV_GROUPS.items.key から自動収集
 * @graph-connects PAGELESS_PERMISSIONS [references] 画面を持たない機能の権限キーを収集
 */
export const ALL_PAGE_KEYS: readonly string[] = [
  ...NAV_GROUPS.flatMap((g) => g.items.flatMap((i) => navItemPermissionKeys(i))),
  ...PAGELESS_PERMISSIONS.map((p) => p.key),
];

/**
 * ナビに項目を出さないが、権限ガードの対象にするページ。
 *
 * ガードはナビ項目の path で前方一致するが、**兄弟ページのパスがナビ項目の配下に
 * ならない**ことがある。例えば `/coorde-capacity` は `/coorde` の次の文字が `/` では
 * なく `-` のため `/coorde` 配下として一致しない。ここに書き漏らすとガードが素通りし、
 * 権限の無いユーザーに画面のシェルだけ見えてしまう。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ナビに出さないページも権限ガードの対象に含める対応表。ナビ項目の前方一致では拾えないパスをここで補う。
 * @graph-connects none
 */
export const GUARDED_EXTRA_PATHS: readonly { path: string; key: string }[] = [
  // 在庫状況。ナビは /coorde に集約したが、同じ coorde 権限でガードし続ける
  { path: '/coorde-capacity', key: 'coorde' },
];

/**
 * パスがそのページの権限キーに一致するか判定する。
 * 完全一致か、配下（`path` + `/`）のときだけ一致とみなす。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ページのパスと現在地が同じページを指すかを判定する。
 * @graph-connects none
 */
function matchesPagePath(currentPath: string, pagePath: string): boolean {
  return currentPath === pagePath || currentPath.startsWith(`${pagePath}/`);
}

/**
 * 現在のパスに対応する権限キーを返す。どのページにも属さなければ `undefined`。
 *
 * より限定的な `GUARDED_EXTRA_PATHS` を先に見る。ナビ項目の前方一致が
 * 意図せず先に当たると、専用に指定した権限キーが無視されるため。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 現在開いているページに必要な権限キーを引く。権限ガードはこの結果で遷移可否を決める。
 * @graph-connects NAV_GROUPS [references] ナビ項目のパスと権限キー
 * @graph-connects GUARDED_EXTRA_PATHS [references] ナビに出さないページのパスと権限キー
 */
export function resolvePagePermissionKey(
  currentPath: string,
): string | readonly string[] | undefined {
  // GUARDED_EXTRA_PATHS は単一キーのみ。ナビ項目は追加権限を含めて返す
  const extra = GUARDED_EXTRA_PATHS.find((entry) => matchesPagePath(currentPath, entry.path));
  if (extra !== undefined) return extra.key;

  const item = NAV_GROUPS.flatMap((g) => g.items).find((i) => matchesPagePath(currentPath, i.path));
  return item === undefined ? undefined : navItemPermissionKeys(item);
}

/**
 * ナビ項目に紐づく権限キーを全て返す（本体 + 追加分）。
 *
 * 権限管理画面の選択肢もこの一覧から作る。追加権限を列挙し忘れると、その権限を
 * 誰にも付与できないまま「持っていれば見える」だけのキーになる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ナビ項目に紐づく権限キー（本体と追加分）を列挙する。権限管理画面の選択肢とページガードの両方がこれを使う。
 * @graph-connects none
 */
export function navItemPermissionKeys(item: NavItemConfig): string[] {
  return [item.key, ...(item.extraPermissions ?? []).map((p) => p.key)];
}

/**
 * ページやナビ項目に設定された権限キーを、現在のユーザーが満たすかを判定する。
 *
 * 配列が渡された場合は「いずれか1つでも持っていれば満たす」とみなす。会員一覧のように
 * 本番用とSTG用で別々の権限があり、片方だけでもページに入れる必要がある画面のための拡張。
 * 文字列が渡された場合の挙動は従来と変わらない。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ページに必要な権限キー（単一または複数）を現在のユーザーが満たすかを判定する。複数指定はいずれか1つを満たせばよい。
 * @graph-connects none
 */
export function satisfiesPagePermission(
  key: string | readonly string[] | undefined,
  hasPermission: (key: string) => boolean,
): boolean {
  if (key === undefined) return true;
  if (typeof key === 'string') return hasPermission(key);
  return key.some((k) => hasPermission(k));
}
